import { apiClient, buildQuery } from '~/api/client'
import type {
    AdminDashboard,
    AdminDashboardAlert,
    AdminDashboardDetail,
    AdminReading,
    MonitoringRiskResponse,
} from '~/api/types'

// 모니터링 읽기 API. 모니터링 "데이터" 탭에서 대시보드 목록·상세를 소비한다.

export interface ListDashboardsParams {
    q?: string
    limit?: number
    offset?: number
}

/** GET /admin/api/services/monitoring/dashboards — 대시보드 목록(페이지네이션). */
export function listDashboards(params: ListDashboardsParams = {}) {
    const query = buildQuery({
        q: params.q,
        limit: params.limit,
        offset: params.offset,
    })
    return apiClient.get<AdminDashboard[]>(
        `/services/monitoring/dashboards${query}`
    )
}

/** GET /admin/api/services/monitoring/dashboards/{id} — 대시보드 상세. */
export function getDashboard(id: string) {
    return apiClient.get<AdminDashboardDetail>(
        `/services/monitoring/dashboards/${id}`
    )
}

/** GET /admin/api/services/monitoring/risk — 위험 관제(요약 + 위험순 행, 읽기 전용). */
export function getMonitoringRisk() {
    return apiClient.get<MonitoringRiskResponse>('/services/monitoring/risk')
}

// 모니터링 "제어" API. 상세 화면에서 알림 처리·통지 설정·토큰 폐기에 쓴다.

/** 알림 처리 상태(요청 본문). 읽기 응답의 acknowledged/closed 등 표기와는 별개. */
export type AlertStatus = 'unread' | 'ack' | 'resolved'

/** 통지 설정 갱신 입력(부분 갱신). 빈 문자열은 호출부에서 null로 정리한다. */
export interface NotifySettingsInput {
    notifyEmail?: string | null
    notifyEmailOn?: boolean
}

/** POST .../ingest-token/revoke 응답. */
export interface RevokeIngestTokenResult {
    revoked: boolean
}

/** PATCH .../dashboards/{id}/alerts/{alertId} {status} — 알림 처리 상태 변경(확인/해결). */
export function setAlertStatus(
    dashboardId: string,
    alertId: string,
    status: AlertStatus
) {
    return apiClient.patch<AdminDashboardAlert>(
        `/services/monitoring/dashboards/${dashboardId}/alerts/${alertId}`,
        { status }
    )
}

/** PUT .../dashboards/{id}/notify-settings — 통지 설정(이메일·전화·on/off) 갱신. */
export function updateNotifySettings(
    dashboardId: string,
    body: NotifySettingsInput
) {
    return apiClient.put<AdminDashboardDetail>(
        `/services/monitoring/dashboards/${dashboardId}/notify-settings`,
        body
    )
}

/** POST .../dashboards/{id}/ingest-token/revoke — 적재 토큰 폐기(superadmin 전용). */
export function revokeIngestToken(dashboardId: string) {
    return apiClient.post<RevokeIngestTokenResult>(
        `/services/monitoring/dashboards/${dashboardId}/ingest-token/revoke`
    )
}

// 모니터링 측정값(readings) 관리 API. 상세 화면의 "측정값" 섹션에서 소비한다.

export interface ListReadingsParams {
    fieldId?: string
    q?: string
    limit?: number
    offset?: number
}

/** 측정값 수정 입력. 값/단위만 갱신하며 측정시각은 불변(부분 갱신). */
export interface UpdateReadingInput {
    value?: number
    unit?: string
}

/** GET .../dashboards/{id}/readings — 개별 측정값 목록(measured_at desc, 검색·페이지네이션). */
export function listReadings(
    dashboardId: string,
    params: ListReadingsParams = {}
) {
    const query = buildQuery({
        fieldId: params.fieldId,
        q: params.q,
        limit: params.limit,
        offset: params.offset,
    })
    return apiClient.get<AdminReading[]>(
        `/services/monitoring/dashboards/${dashboardId}/readings${query}`
    )
}

/** PATCH .../dashboards/{id}/readings/{rid} {value?, unit?} — 측정값 수정(알림 재평가). */
export function updateReading(
    dashboardId: string,
    readingId: string,
    body: UpdateReadingInput
) {
    return apiClient.patch<AdminReading>(
        `/services/monitoring/dashboards/${dashboardId}/readings/${readingId}`,
        body
    )
}

/** DELETE .../dashboards/{id}/readings/{rid} — 측정값 삭제(superadmin 전용·알림 재평가). */
export function deleteReading(dashboardId: string, readingId: string) {
    return apiClient.delete<void>(
        `/services/monitoring/dashboards/${dashboardId}/readings/${readingId}`
    )
}
